import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { pricingTiers } from '../../data/mock';

const PricingSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900 mb-4">
            Choose how deep you want to go
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Start free. Upgrade only if you want the full insider experience.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {pricingTiers.map((tier, index) => (
            <div
              key={tier.id}
              className={`relative rounded-2xl p-8 transition-all duration-700 ${
                tier.highlighted
                  ? 'bg-stone-900 text-white shadow-xl scale-105 md:scale-110'
                  : 'bg-white text-stone-900 shadow-sm'
              } ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-white text-sm font-medium rounded-full">
                  Most Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className={`text-lg font-medium mb-2 ${tier.highlighted ? 'text-white' : 'text-stone-900'}`}>
                  {tier.name}
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-serif ${tier.highlighted ? 'text-white' : 'text-stone-900'}`}>
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span className={`text-sm ${tier.highlighted ? 'text-white/70' : 'text-stone-500'}`}>
                      /{tier.period}
                    </span>
                  )}
                </div>
                <p className={`text-sm mt-2 ${tier.highlighted ? 'text-white/70' : 'text-stone-500'}`}>
                  {tier.description}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      tier.highlighted ? 'text-amber-400' : 'text-amber-600'
                    }`} />
                    <span className={`text-sm ${tier.highlighted ? 'text-white/90' : 'text-stone-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to="/destinations"
                className={`block w-full py-3 rounded-full text-center font-medium transition-colors ${
                  tier.highlighted
                    ? 'bg-white text-stone-900 hover:bg-amber-50'
                    : 'bg-stone-900 text-white hover:bg-stone-800'
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Trust Note */}
        <p
          className={`text-center text-stone-500 text-sm mt-12 transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          7-day money-back guarantee. No questions asked.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
